/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useRef, useState } from 'react';
import { styled, Container, Row, Col } from '@teko/ds-desktop';
import { BannerEntity } from '@teko/ecommerce';
import NextBtnSvg from './Next.svg';
import PrevBtnSvg from './Previous.svg';
import { BannerSlides } from 'components/desktop/organisms/BannerSlides';
import { BannerProps, MegaMenu } from '@teko/teko-ecommerce';
import { MegaMenuItemInterface } from '@teko/types/pb';
import { getTrackingData } from '@teko/tracking';

import dynamic from 'next/dynamic';

const EnhancedImage = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.EnhancedImage),
);

const FeaturedBanners = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.FeaturedBanners),
);

const sliderHeight = 566;
const maxWidth = 767;

const Wrapper = styled.div`
  position: relative;
  content-visibility: auto;
  margin-bottom: 24px;
  .swiper-pagination {
    bottom: 6rem !important;
  }
  min-height: 600px;
  @media screen and (max-width: ${maxWidth}px) {
    height: auto;
  }
`;

Wrapper.displayName = 'AboveTheFold';

const StyledBannerSlides = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 0;
`;

const FourBannerWrapper = styled(Row)`
  margin-top: -72px;
`;

const NavBtnWrapper = styled.img(
  ({ show, left, right }: NavBtnWrapperInterface) => `
  opacity: ${show ? '1' : '0'};
  z-index: 549;
  cursor: pointer;
  transition: 0.1s;
  position: absolute;
  top: 39.5%;
  ${left ? `left: ${left};` : ''}
  ${right ? `right: ${right};` : ''}
`,
);

const RightBannerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface NavBtnWrapperInterface {
  show: boolean;
  left?: string;
  right?: string;
}
export interface AboveTheFoldProps {
  menuItems: Array<MegaMenuItemInterface>;
  slideBanners: Array<BannerEntity>;
  topRightBanners: Array<BannerEntity>;
  horizontalFourBanners: Array<BannerEntity>;
}

export const renderMegaMenu = (menuItems: MegaMenuItemInterface[]) => {
  if (!menuItems?.length) return;
  return <MegaMenu menuItems={menuItems} type="vertical" />;
};

export const AboveTheFold: React.FunctionComponent<AboveTheFoldProps> = ({
  menuItems,
  slideBanners = [],
  topRightBanners = [],
  horizontalFourBanners = [],
}: AboveTheFoldProps) => {
  const sliderRef = useRef(null);

  const [showNavBtns, setShowNavBtns] = useState(false);

  if (!menuItems.length && !slideBanners.length && !topRightBanners.length)
    return null;

  const isOnlySlideBanner = !menuItems.length && !topRightBanners.length;

  return (
    <Wrapper>
      <Container>
        <Row justify="space-around" style={{ height: `${sliderHeight}px` }}>
          <Col span={2} paddingTop="s16">
            {renderMegaMenu(menuItems)}
            <NavBtnWrapper
              onMouseEnter={() => setShowNavBtns(true)}
              show={showNavBtns}
              right={!isOnlySlideBanner && '-46px'}
              left={isOnlySlideBanner && '0'}
              onClick={() => {
                if (sliderRef.current) {
                  sliderRef.current.glide.go('<');
                }
              }}
              src={PrevBtnSvg}
              alt=""
            />
          </Col>
          <div style={{ flex: 1 }}>
            <StyledBannerSlides
              onMouseEnter={() => setShowNavBtns(true)}
              onMouseLeave={() => setShowNavBtns(false)}
            >
              <BannerSlides
                imageWidth={1920}
                banners={slideBanners}
                sliderRef={sliderRef}
                prioritized={true}
              />
            </StyledBannerSlides>
          </div>
          <Col span={2} paddingTop="s16">
            <NavBtnWrapper
              onMouseEnter={() => setShowNavBtns(true)}
              show={showNavBtns}
              onClick={() => {
                if (sliderRef.current) {
                  sliderRef.current.glide.go('>');
                }
              }}
              left={!isOnlySlideBanner && '-48px'}
              right={isOnlySlideBanner && '0'}
              src={NextBtnSvg}
              alt=""
            />
            {topRightBanners.length !== 0 && (
              <RightBannerWrapper>
                {topRightBanners.map((banner, index) => (
                  <EnhancedImage
                    key={index}
                    link={{
                      href: banner.targetPath,
                    }}
                    tracking={getTrackingData({
                      isTrackContent: true,
                      regionName: 'topRightBannerHomepage',
                      contentName: banner.campaignName,
                      index: index,
                      target: banner.targetPath,
                    })}
                    src={banner.image}
                    borderRadius={8}
                    height={220}
                    objectFit="cover"
                    googleImageParams={{
                      width: 300,
                    }}
                    hover="zoom"
                    alt={banner.campaignName || banner.description || ''}
                    lazy={false}
                    fetchPriority="high"
                  />
                ))}
              </RightBannerWrapper>
            )}
          </Col>
        </Row>
        {!!horizontalFourBanners.length && (
          <FourBannerWrapper>
            <Col span={12}>
              <FeaturedBanners
                prioritized={true}
                regionName="bottomBanner"
                banners={horizontalFourBanners.map((banner): BannerProps => {
                  return {
                    src: banner.image,
                    link: {
                      href: banner.targetPath,
                    },
                    name: banner.campaignName,
                    lazy: false,
                  };
                })}
              />
            </Col>
          </FourBannerWrapper>
        )}
      </Container>
    </Wrapper>
  );
};
